import React from 'react';
import {
  Gauge,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useHtmlAudioPlayer, PLAYBACK_RATES } from '../../hooks/useHtmlAudioPlayer';

const toFaDigits = (value: string | number) =>
  String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const PLAYER_STYLES = `
  .pro-audio-range::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.35), 0 3px 10px rgba(0, 0, 0, 0.4);
    transition: transform 0.15s ease;
  }
  .pro-audio-range:hover::-webkit-slider-thumb { transform: scale(1.18); }
  .pro-audio-range::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.35), 0 3px 10px rgba(0, 0, 0, 0.4);
  }
  .pro-vol-range::-webkit-slider-thumb {
    appearance: none;
    width: 11px;
    height: 11px;
    border-radius: 9999px;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  }
  .pro-vol-range::-moz-range-thumb {
    width: 11px;
    height: 11px;
    border-radius: 9999px;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  }
  @keyframes pro-eq {
    0%, 100% { transform: scaleY(0.3); }
    50% { transform: scaleY(1); }
  }
  .pro-eq-bar {
    height: 12px;
    transform-origin: bottom;
    animation: pro-eq 0.9s ease-in-out infinite;
  }
  @keyframes pro-pulse {
    0% { transform: scale(0.95); opacity: 0.5; }
    70% { transform: scale(1.4); opacity: 0; }
    100% { opacity: 0; }
  }
  .pro-pulse-ring { animation: pro-pulse 2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
  @keyframes pro-glow {
    0%, 100% { opacity: 0.45; }
    50% { opacity: 0.8; }
  }
  .pro-glow { animation: pro-glow 3s ease-in-out infinite; }
`;

export type CourseAudioPlayerProps = {
  src: string | null;
  mediaKey: string;
  title?: string;
  subtitle?: string;
  coverUrl?: string | null;
  knownDurationSeconds?: number;
  variant?: 'compact' | 'full';
  className?: string;
  onError?: (message: string) => void;
  loadSavedProgress?: () => number;
  onProgressSave?: (seconds: number) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  /** با پایان فایل صدا زده می‌شود — صفحه تصمیم می‌گیرد بعدی را بیاورد یا نه */
  onEnded?: () => void;
  /** این فایل به‌محض آماده شدن خودش پخش شود (قطعه‌ای که خودکار آمده) */
  autoPlay?: boolean;
  jumpSeconds?: number;
  showVolume?: boolean;
  showClose?: boolean;
  onClose?: () => void;
};

const CourseAudioPlayer: React.FC<CourseAudioPlayerProps> = ({
  src,
  mediaKey,
  title,
  subtitle,
  coverUrl,
  knownDurationSeconds,
  variant = 'compact',
  className = '',
  onError,
  loadSavedProgress,
  onProgressSave,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  onEnded,
  autoPlay = false,
  jumpSeconds = 30,
  showVolume = variant === 'full',
  showClose = false,
  onClose,
}) => {
  const player = useHtmlAudioPlayer({
    src,
    mediaKey,
    knownDurationSeconds,
    loadSavedProgress,
    onProgressSave,
    autoPlay,
    onEnded,
  });

  const [lastVolume, setLastVolume] = React.useState(1);

  const handleAudioError = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const el = e.currentTarget;
    const code = el.error?.code;
    let message = 'خطا در پخش فایل صوتی.';
    if (code === 1) message += ' فایل یافت نشد.';
    else if (code === 2) message += ' خطا در اتصال به سرور.';
    else if (code === 3) message += ' فایل خراب است یا فرمت پشتیبانی نمی‌شود.';
    else if (code === 4) message += ' فرمت قابل پخش نیست.';
    onError?.(message);
  };

  if (!src) {
    return (
      <div className={className}>
        <div className="rounded-xl border border-red-400/30 bg-red-950/20 px-3 py-2 text-xs text-red-300">
          خطا در بارگذاری فایل صوتی
        </div>
      </div>
    );
  }

  const isFull = variant === 'full';
  const progressValue = player.progressPercent;
  const showTrackNav = Boolean(onPrevious || onNext);
  const isMuted = player.volume === 0;
  const jumpLabel = toFaDigits(jumpSeconds);

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    player.seekByPercent(parseFloat(e.target.value));
  };

  const toggleMute = () => {
    if (isMuted) {
      player.setVolume(lastVolume > 0 ? lastVolume : 1);
    } else {
      setLastVolume(player.volume);
      player.setVolume(0);
    }
  };

  const trackFill = `linear-gradient(90deg, #6C8BFF 0%, #8E97F5 ${progressValue}%, rgba(255,255,255,0.14) ${progressValue}%, rgba(255,255,255,0.14) 100%)`;

  const eqBars = (
    <span className="flex h-3 items-end gap-[2px]" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={`w-[2px] rounded-full bg-[#a5b4fc] ${player.isPlaying ? 'pro-eq-bar' : 'h-1'}`}
          style={player.isPlaying ? { animationDelay: `${i * 0.18}s` } : undefined}
        />
      ))}
    </span>
  );

  return (
    <div
      className={`relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#212c52] via-[#273263] to-[#1e284b] text-white shadow-[0_30px_70px_-34px_rgba(30,40,80,0.85)] ${
        isFull ? 'p-5 sm:p-7' : 'p-4 sm:p-5'
      } ${className}`}
      dir="rtl"
    >
      {/* هاله‌های نور محیطی */}
      <div aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full bg-[#3a5bd0]/40 blur-[75px]" />
      <div aria-hidden className="pointer-events-none absolute -left-20 -bottom-28 h-60 w-60 rounded-full bg-[#6366f1]/28 blur-[85px]" />

      <audio
        ref={player.audioRef}
        preload="auto"
        playsInline
        className="hidden"
        onError={handleAudioError}
      />

      {/* هدر: کاور + عنوان + وضعیت */}
      {isFull ? (
        <div className="relative mb-6 flex items-center gap-4">
          {coverUrl ? (
            <div className="relative shrink-0">
              <span
                aria-hidden
                className={`absolute -inset-1 rounded-[1.35rem] bg-gradient-to-br from-[#3a5bd0] to-[#6366f1] opacity-50 blur-md ${
                  player.isPlaying ? 'pro-glow' : ''
                }`}
              />
              <img
                src={coverUrl}
                alt="استاد دوره"
                className="relative aspect-square h-[4.5rem] w-[4.5rem] rounded-[1.25rem] object-cover ring-1 ring-white/25 sm:h-24 sm:w-24"
              />
            </div>
          ) : null}
          <div className="min-w-0 flex-1 text-right">
            <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold text-slate-100 ring-1 ring-inset ring-white/15 sm:text-[11px]">
              {eqBars}
              {player.isPlaying ? 'در حال پخش' : player.isBuffering ? 'در حال بارگذاری…' : 'آماده پخش'}
            </div>
            {title ? (
              <p className="truncate text-base font-extrabold leading-snug sm:text-xl">{title}</p>
            ) : null}
            {subtitle ? (
              <p className="mt-0.5 truncate text-xs text-slate-300/80 sm:text-sm">{subtitle}</p>
            ) : null}
          </div>
          {showClose && onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-full border border-white/15 text-slate-300 transition hover:border-white/45 hover:text-white"
              aria-label="بستن پخش‌کننده"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      ) : (
        (showClose || title || subtitle || coverUrl) && (
          <div className="relative mb-4 flex items-start gap-3">
            {coverUrl ? (
              <img
                src={coverUrl}
                alt="استاد دوره"
                className="aspect-square h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-white/15 sm:h-14 sm:w-14"
              />
            ) : null}
            <div className="min-w-0 flex-1 text-right">
              <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-slate-100 ring-1 ring-inset ring-white/15">
                {eqBars}
                {player.isPlaying ? 'در حال پخش' : 'آماده پخش'}
              </div>
              {title ? <p className="truncate text-sm font-bold sm:text-base">{title}</p> : null}
              {subtitle ? (
                <p className="mt-0.5 truncate text-[11px] text-slate-300/80 sm:text-xs">{subtitle}</p>
              ) : null}
            </div>
            {showClose && onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/20 text-slate-300 transition hover:border-white/45 hover:text-white"
                aria-label="بستن پخش‌کننده"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        )
      )}

      {/* نوار پیشرفت */}
      <div className="relative flex items-center gap-2.5 text-[11px] text-slate-300/85 sm:text-xs" dir="ltr">
        <span className="min-w-[2.6rem] font-mono font-semibold tabular-nums text-slate-200">
          {player.formatTime(player.currentTime)}
        </span>
        <input
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={progressValue}
          onChange={handleSeekChange}
          disabled={!player.canControl && player.duration <= 0}
          className="pro-audio-range h-1.5 w-full cursor-pointer appearance-none rounded-full disabled:cursor-not-allowed disabled:opacity-40"
          style={{ background: trackFill }}
          aria-label="پیشرفت پخش"
        />
        <span className="min-w-[2.6rem] text-right font-mono tabular-nums text-slate-400">
          {player.duration > 0 ? player.formatTime(player.duration) : '--:--'}
        </span>
      </div>

      {/* کنترل‌های اصلی */}
      <div className={`relative mt-4 flex items-center justify-center gap-2 sm:mt-5 sm:gap-3.5 ${isFull ? '' : 'gap-2.5'}`}>
        {showTrackNav ? (
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-200 transition hover:border-[#a5b4fc] hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-25"
            aria-label="فایل بعدی"
          >
            <SkipForward className="h-4 w-4" />
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => player.skip(-jumpSeconds)}
          disabled={!player.canControl}
          className="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-slate-200 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-25"
          aria-label={`${jumpSeconds} ثانیه عقب`}
        >
          <RotateCcw className="h-9 w-9" strokeWidth={1.4} />
          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black tabular-nums">
            {jumpLabel}
          </span>
        </button>

        <button
          type="button"
          onClick={player.togglePlayPause}
          disabled={!src}
          className={`group relative inline-flex shrink-0 items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40 ${
            isFull ? 'h-16 w-16' : 'h-12 w-12'
          }`}
          aria-label={player.isPlaying ? 'توقف' : 'پخش'}
        >
          {player.isPlaying ? (
            <span className="pro-pulse-ring absolute inset-0 rounded-full bg-[#6366f1]/45" aria-hidden />
          ) : null}
          <span
            className={`relative inline-flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-[#2f5ae0] to-[#6366f1] shadow-[0_10px_30px_-8px_rgba(79,70,229,0.65)] ring-1 ring-white/20 transition-transform duration-200 group-hover:scale-[1.05] group-active:scale-95 ${
              isFull ? 'h-16 w-16' : 'h-12 w-12'
            }`}
          >
            {player.isBuffering && !player.isPlaying ? (
              <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : player.isPlaying ? (
              <Pause className={isFull ? 'h-6 w-6' : 'h-5 w-5'} fill="currentColor" />
            ) : (
              <Play className={`${isFull ? 'h-6 w-6' : 'h-5 w-5'} translate-x-[1px]`} fill="currentColor" />
            )}
          </span>
        </button>

        <button
          type="button"
          onClick={() => player.skip(jumpSeconds)}
          disabled={!player.canControl}
          className="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-slate-200 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-25"
          aria-label={`${jumpSeconds} ثانیه جلو`}
        >
          <RotateCw className="h-9 w-9" strokeWidth={1.4} />
          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black tabular-nums">
            {jumpLabel}
          </span>
        </button>

        {showTrackNav ? (
          <button
            type="button"
            onClick={onPrevious}
            disabled={!hasPrevious}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-200 transition hover:border-[#a5b4fc] hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-25"
            aria-label="فایل قبلی"
          >
            <SkipBack className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* ردیف پایین: سرعت + صدا */}
      <div className="relative mt-5 flex items-center justify-between gap-3 border-t border-white/10 pt-4">
        <div className="flex items-center gap-1.5">
          <Gauge className="h-3.5 w-3.5 shrink-0 text-slate-400/80" strokeWidth={2} aria-hidden />
          <div className="flex items-center gap-1">
            {PLAYBACK_RATES.map((rate) => (
              <button
                key={rate}
                type="button"
                onClick={() => player.setPlaybackRate(rate)}
                className={`inline-flex h-7 min-w-[2.5rem] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums transition sm:min-w-[2.8rem] sm:text-[11px] ${
                  player.playbackRate === rate
                    ? 'bg-gradient-to-br from-[#2f5ae0] to-[#6366f1] text-white shadow-[0_4px_14px_-4px_rgba(79,70,229,0.65)]'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
                aria-label={`سرعت ${rate}x`}
                aria-pressed={player.playbackRate === rate}
              >
                {toFaDigits(rate)}x
              </button>
            ))}
          </div>
        </div>

        {showVolume ? (
          <div className="flex items-center gap-2" dir="ltr">
            <button
              type="button"
              onClick={toggleMute}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-300 transition hover:bg-white/5 hover:text-white"
              aria-label={isMuted ? 'قطع بی‌صدا' : 'بی‌صدا'}
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={player.volume}
              onChange={(e) => player.setVolume(parseFloat(e.target.value))}
              className="pro-vol-range h-1 w-16 cursor-pointer appearance-none rounded-full sm:w-24"
              style={{
                background: `linear-gradient(to right, #a5b4fc 0%, #a5b4fc ${player.volume * 100}%, rgba(255,255,255,0.12) ${player.volume * 100}%, rgba(255,255,255,0.12) 100%)`,
              }}
              aria-label="بلندی صدا"
            />
          </div>
        ) : null}
      </div>

      <style>{PLAYER_STYLES}</style>
    </div>
  );
};

export default CourseAudioPlayer;
